/**
 * HarborDropdown
 *
 * A single-select dropdown that positions its list inline (absolute, inside the
 * component's own View) instead of inside a React Native Modal.  This avoids the
 * scroll-offset bug that appears when LHDropDown's Modal uses window.scrollY
 * (always 0) to position itself while the parent is a React Native ScrollView.
 *
 * API is a subset of LHDropDown so it can be dropped in without changing callers.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Platform,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { useDarkStyles, useDarkColor } from '../../theme/useDarkStyles';

export interface HarborDropdownItem {
  id: string | number;
  displayText: string;
  [key: string]: any;
}

interface HarborDropdownProps {
  label?: string;
  isRequired?: boolean;
  placeholder?: string;
  childList: HarborDropdownItem[];
  selectedValue: HarborDropdownItem | null;
  setSelectedValue: (item: HarborDropdownItem | null) => void;
  isClearable?: boolean;
  readOnly?: boolean;
  style?: any;
  size?: 'small' | 'medium' | 'large';
  openTrigger?: number;
}

const HarborDropdown: React.FC<HarborDropdownProps> = ({
  label = '',
  isRequired = false,
  placeholder = 'Select…',
  childList = [],
  selectedValue = null,
  setSelectedValue,
  isClearable = false,
  readOnly = false,
  style,
  size = 'medium',
  openTrigger = 0,
}) => {
  const dc = useDarkColor();
  const ddStyles = useDarkStyles(baseDdStyles);
  const { t } = useTranslation('harbor');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<TextInput>(null);

  const toggle = useCallback(() => {
    if (readOnly) return;
    if (!isOpen) {
      setSearch('');
      setIsOpen(true);
      // Focus search box on next tick
      setTimeout(() => searchRef.current?.focus(), 50);
    } else {
      setIsOpen(false);
    }
  }, [isOpen, readOnly]);

  useEffect(() => {
    if (openTrigger <= 0 || readOnly) return;
    setSearch('');
    setIsOpen(true);
    setTimeout(() => searchRef.current?.focus(), 50);
  }, [openTrigger, readOnly]);

  const handleSelect = (item: HarborDropdownItem) => {
    setSelectedValue(item);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e: any) => {
    if (e?.stopPropagation) e.stopPropagation();
    setSelectedValue(null);
    setIsOpen(false);
    setSearch('');
  };

  const filtered = childList.filter(item =>
    item.displayText.toLowerCase().includes(search.toLowerCase()),
  );

  const displayText = selectedValue ? selectedValue.displayText : placeholder;
  const isPlaceholder = !selectedValue;

  return (
    <View style={[ddStyles.container, style]}>
      {!!label && (
        <Text style={ddStyles.label}>
          {label}
          {isRequired && <Text style={ddStyles.required}> *</Text>}
        </Text>
      )}

      {/* Trigger + dropdown list share this relative container */}
      <View style={ddStyles.anchor}>
        <TouchableOpacity
          style={[
            ddStyles.trigger,
            size === 'small' && ddStyles.triggerSm,
            size === 'large' && ddStyles.triggerLg,
            readOnly && ddStyles.triggerDisabled,
          ]}
          onPress={toggle}
          disabled={readOnly}
          activeOpacity={0.8}
        >
          <Text
            style={[ddStyles.triggerText, isPlaceholder && ddStyles.placeholderText]}
            numberOfLines={1}
          >
            {displayText}
          </Text>

          {isClearable && selectedValue && (
            <TouchableOpacity style={ddStyles.clearBtn} onPress={handleClear}>
              <MaterialIcons name="close" size={18} color={dc("#495057")} />
            </TouchableOpacity>
          )}

          <MaterialIcons
            name={isOpen ? 'arrow-drop-up' : 'arrow-drop-down'}
            size={20}
            color={dc("#495057")}
          />
        </TouchableOpacity>

        {isOpen && (
          <View style={ddStyles.menu}>
            {/* Search row */}
            <View style={ddStyles.searchRow}>
              <MaterialIcons name="search" size={18} color={dc("#6C757D")} />
              <TextInput
                ref={searchRef}
                style={ddStyles.searchInput}
                placeholder={t('common.search')}
                placeholderTextColor={dc("#6C757D")}
                value={search}
                onChangeText={setSearch}
              />
            </View>

            {/* Items list */}
            <ScrollView
              style={ddStyles.list}
              keyboardShouldPersistTaps="handled"
              nestedScrollEnabled
            >
              {filtered.length === 0 ? (
                <Text style={ddStyles.empty}>{t('common.no_results_found')}</Text>
              ) : (
                filtered.map(item => {
                  const isSelected = selectedValue?.id === item.id;
                  return (
                    <TouchableOpacity
                      key={String(item.id)}
                      style={[ddStyles.item, isSelected && ddStyles.itemSelected]}
                      onPress={() => handleSelect(item)}
                    >
                      <Text style={ddStyles.itemText}>{item.displayText}</Text>
                      {isSelected && (
                        <MaterialIcons name="check" size={18} color={dc("#0066CC")} />
                      )}
                    </TouchableOpacity>
                  );
                })
              )}
            </ScrollView>
          </View>
        )}
      </View>
    </View>
  );
};

const baseDdStyles = StyleSheet.create({
  container: {
    marginBottom: 0,
    width: '100%',
  },
  label: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#343A40',
    marginBottom: 6,
  },
  required: {
    color: '#DC3545',
  },
 
  anchor: {
    position: 'relative',
    zIndex: 1000,
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#DEE2E6',
    borderRadius: 4,
    backgroundColor: '#fff',
    minHeight: 40,
  },
  triggerSm: { minHeight: 32, paddingVertical: 6 },
  triggerLg: { minHeight: 48, paddingVertical: 14 },
  triggerDisabled: { opacity: 0.5 },
  triggerText: {
    flex: 1,
    fontSize: 16,
    color: '#343A40',
    marginRight: 4,
  },
  placeholderText: {
    color: '#6C757D',
  },
  clearBtn: {
    marginRight: 4,
  },
  // Dropdown menu — absolute, renders directly below the trigger
  menu: {
    position: 'absolute',
    top: '100%' as any,   // sits flush below the trigger
    left: 0,
    right: 0,
    backgroundColor: '#fff',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#DEE2E6',
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
        } as any)
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.15,
          shadowRadius: 8,
          elevation: 8,
          zIndex: 9999,
        }),
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E9ECEF',
    gap: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#343A40',
    paddingVertical: 2,
  },
  list: {
    maxHeight: 200,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F3F5',
  },
  itemSelected: {
    backgroundColor: '#F8F9FA',
  },
  itemText: {
    flex: 1,
    fontSize: 14,
    color: '#343A40',
    marginRight: 8,
  },
  empty: {
    padding: 14,
    textAlign: 'center',
    color: '#6C757D',
    fontSize: 14,
  },
});

export default HarborDropdown;
